import { useState } from "react";
import {
  useModelPlans,
  useUpdateModelPlan,
  useCreateModelPlan,
  useArchiveModelPlan,
} from "@/hooks/use-pricing-admin";

const STARTER_RATE = 0.00900; // $90 / 10k credits (Starter, matches live Stripe checkout)

type Plan = {
  id: number;
  name: string;
  priceCents: number;
  creditsPerMonth: number;
  features: string[];
  isArchived: boolean;
  /** v7.10 — plan entitlements. maxBrands null = unlimited. */
  maxBrands: number | null;
  videoAccess: boolean;
};

type DraftPatch = Partial<Pick<Plan, "name" | "priceCents" | "creditsPerMonth" | "features" | "maxBrands" | "videoAccess">>;

export function PricingPlansTab() {
  const { data, isLoading, error } = useModelPlans();
  const update = useUpdateModelPlan();
  const create = useCreateModelPlan();
  const archive = useArchiveModelPlan();
  const [drafts, setDrafts] = useState<Record<number, DraftPatch>>({});
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: "", priceCents: 0, creditsPerMonth: 0, features: "" });

  if (isLoading) return <div className="p-6 text-sm text-neutral-500">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load plans: {String(error)}</div>;

  const rows: Plan[] = (data?.rows ?? []).slice().sort((a: Plan, b: Plan) => a.priceCents - b.priceCents);

  const draftOf = (p: Plan) => ({
    name: drafts[p.id]?.name ?? p.name,
    priceCents: drafts[p.id]?.priceCents ?? p.priceCents,
    creditsPerMonth: drafts[p.id]?.creditsPerMonth ?? p.creditsPerMonth,
    features: drafts[p.id]?.features ?? p.features,
    maxBrands: drafts[p.id]?.maxBrands !== undefined ? drafts[p.id]!.maxBrands! : p.maxBrands,
    videoAccess: drafts[p.id]?.videoAccess ?? p.videoAccess,
  });

  const isDirty = (p: Plan) => !!drafts[p.id] && Object.keys(drafts[p.id] || {}).length > 0;

  const setDraft = (id: number, patch: DraftPatch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));

  const resetDraft = (id: number) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const save = async (p: Plan) => {
    const d = draftOf(p);
    await update.mutateAsync({
      id: p.id,
      patch: {
        name: d.name,
        priceCents: d.priceCents,
        creditsPerMonth: d.creditsPerMonth,
        features: d.features,
        maxBrands: d.maxBrands,
        videoAccess: d.videoAccess,
      },
    });
    resetDraft(p.id);
  };

  const doArchive = async (p: Plan) => {
    if (!confirm(`Archive "${p.name}"? Existing subscribers keep access; plan becomes invisible to new signups.`)) return;
    try {
      await archive.mutateAsync(p.id);
    } catch (err: any) {
      const msg = String(err?.message ?? "");
      if (msg.includes("409") || msg.includes("plan_has_active_subscribers")) {
        alert(`Cannot archive "${p.name}" — it has active subscribers.`);
      } else {
        alert(`Failed to archive: ${msg}`);
      }
    }
  };

  const doCreate = async () => {
    const features = newPlan.features
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    await create.mutateAsync({
      name: newPlan.name,
      priceCents: newPlan.priceCents,
      creditsPerMonth: newPlan.creditsPerMonth,
      features,
    });
    setNewPlan({ name: "", priceCents: 0, creditsPerMonth: 0, features: "" });
    setCreatingOpen(false);
  };

  return (
    <div className="space-y-4">
      <section className="rounded border border-neutral-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-neutral-500">
              <tr>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">$/month</th>
                <th className="px-4 py-2 font-medium">Credits/mo</th>
                <th className="px-4 py-2 font-medium">$/credit</th>
                <th className="px-4 py-2 font-medium">vs Starter</th>
                <th className="px-4 py-2 font-medium">Max brands</th>
                <th className="px-4 py-2 font-medium">Video</th>
                <th className="px-4 py-2 font-medium">Features</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const d = draftOf(p);
                const dollars = d.priceCents / 100;
                const perCredit = d.creditsPerMonth > 0 ? dollars / d.creditsPerMonth : 0;
                const vsStarter = perCredit > 0 ? ((perCredit - STARTER_RATE) / STARTER_RATE) * 100 : 0;
                const dirty = isDirty(p);

                return (
                  <tr key={p.id} className="border-t border-neutral-100">
                    <td className="px-4 py-2">
                      <input
                        className="w-32 rounded border border-neutral-300 px-2 py-1 font-medium"
                        value={d.name}
                        onChange={(e) => setDraft(p.id, { name: e.target.value })}
                        data-testid={`plan-${p.id}-name`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-20 rounded border border-neutral-300 px-2 py-1 font-mono text-xs"
                        value={dollars.toFixed(2)}
                        onChange={(e) =>
                          setDraft(p.id, { priceCents: Math.round(Number(e.target.value) * 100) })
                        }
                        data-testid={`plan-${p.id}-price`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-28 rounded border border-neutral-300 px-2 py-1 font-mono text-xs"
                        value={d.creditsPerMonth}
                        onChange={(e) => setDraft(p.id, { creditsPerMonth: Number(e.target.value) || 0 })}
                        data-testid={`plan-${p.id}-credits`}
                      />
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-neutral-700">
                      ${perCredit.toFixed(5)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-neutral-700">
                      {vsStarter >= 0 ? `+${vsStarter.toFixed(1)}%` : `${vsStarter.toFixed(1)}%`}
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-16 rounded border border-neutral-300 px-2 py-1 font-mono text-xs"
                        value={d.maxBrands ?? ""}
                        placeholder="∞"
                        title="Blank = unlimited"
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          setDraft(p.id, { maxBrands: raw === "" ? null : Math.max(1, Number(raw) || 1) });
                        }}
                        data-testid={`plan-${p.id}-max-brands`}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={Boolean(d.videoAccess)}
                        title="Plan includes Video Ads once the feature GAs"
                        onChange={(e) => setDraft(p.id, { videoAccess: e.target.checked })}
                        data-testid={`plan-${p.id}-video-access`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-56 rounded border border-neutral-300 px-2 py-1 text-xs"
                        value={(d.features ?? []).join(", ")}
                        onChange={(e) =>
                          setDraft(p.id, {
                            features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                          })
                        }
                        data-testid={`plan-${p.id}-features`}
                        placeholder="feature_1, feature_2"
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {dirty && (
                          <>
                            <button
                              className="rounded border border-neutral-300 bg-white px-2 py-1 text-xs hover:bg-neutral-50"
                              onClick={() => resetDraft(p.id)}
                              disabled={update.isPending}
                            >
                              Cancel
                            </button>
                            <button
                              className="rounded bg-black px-3 py-1 text-xs text-white disabled:opacity-40"
                              onClick={() => save(p)}
                              disabled={update.isPending}
                              data-testid={`plan-${p.id}-save`}
                            >
                              {update.isPending ? "Saving…" : "Save"}
                            </button>
                          </>
                        )}
                        {!dirty && (
                          <button
                            className="rounded border border-neutral-300 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            onClick={() => doArchive(p)}
                            disabled={archive.isPending}
                            data-testid={`plan-${p.id}-archive`}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-2 text-right">
          {creatingOpen ? (
            <div className="flex items-center justify-end gap-2">
              <input
                placeholder="Name"
                className="w-32 rounded border border-neutral-300 px-2 py-1 text-sm"
                value={newPlan.name}
                onChange={(e) => setNewPlan((s) => ({ ...s, name: e.target.value }))}
              />
              <input
                placeholder="$/mo"
                className="w-20 rounded border border-neutral-300 px-2 py-1 font-mono text-xs"
                value={(newPlan.priceCents / 100).toFixed(2)}
                onChange={(e) =>
                  setNewPlan((s) => ({ ...s, priceCents: Math.round(Number(e.target.value) * 100) || 0 }))
                }
              />
              <input
                placeholder="Credits/mo"
                className="w-28 rounded border border-neutral-300 px-2 py-1 font-mono text-xs"
                value={newPlan.creditsPerMonth || ""}
                onChange={(e) => setNewPlan((s) => ({ ...s, creditsPerMonth: Number(e.target.value) || 0 }))}
              />
              <input
                placeholder="feat_1, feat_2"
                className="w-56 rounded border border-neutral-300 px-2 py-1 text-xs"
                value={newPlan.features}
                onChange={(e) => setNewPlan((s) => ({ ...s, features: e.target.value }))}
              />
              <button
                className="rounded border border-neutral-300 bg-white px-2 py-1 text-xs hover:bg-neutral-50"
                onClick={() => setCreatingOpen(false)}
              >
                Cancel
              </button>
              <button
                className="rounded bg-black px-3 py-1 text-xs text-white disabled:opacity-40"
                onClick={doCreate}
                disabled={create.isPending || !newPlan.name.trim() || newPlan.creditsPerMonth <= 0}
              >
                {create.isPending ? "Creating…" : "Create plan"}
              </button>
            </div>
          ) : (
            <button
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
              onClick={() => setCreatingOpen(true)}
              data-testid="plan-create-open"
            >
              + Add plan
            </button>
          )}
        </div>
      </section>

      <div className="text-xs text-neutral-500">
        Price changes apply on next renewal, never retroactively. Archived plans remain for existing subscribers.
      </div>
    </div>
  );
}
